import { Component, Input, Output, EventEmitter } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormBuilder,
  NG_VALUE_ACCESSOR,
  Validators,
  ValidatorFn
} from '@angular/forms';
import { setControlEnabled } from '../../../../../../../src/app/website/utils/functions/set-control-enabled';
import { Address } from '../../../fact-find/services/fact-find-api.service';

export const addressValidator: ValidatorFn = (control: AbstractControl) => {
  const address: Address = control.value;

  const isValid = !!(
    address &&
    address.postcode &&
    address.postcode.length &&
    address.house &&
    address.house.length &&
    address.street &&
    address.street.length &&
    address.city &&
    address.city.length &&
    address.country &&
    address.country.length
  );

  return isValid ? null : { address: true };
};

export const addressWithRegionValidator: ValidatorFn = (control: AbstractControl) => {
  const address: Address = control.value;

  const isValid = !!(
    address &&
    address.postcode &&
    address.postcode.length &&
    address.house &&
    address.house.length &&
    address.street &&
    address.street.length &&
    address.city &&
    address.city.length &&
    address.country &&
    address.country.length &&
    (!address._isUk || (address.region && address.region.length))
  );

  return isValid ? null : { address: true };
};

@Component({
  selector: 'hfc-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: AddressComponent,
      multi: true
    }
  ]
})
export class AddressComponent implements ControlValueAccessor {
  @Input()
  public withRegion = false;

  @Output()
  public addressCleared = new EventEmitter<void>();

  public locationMap = new Map<boolean, string>([
    [true, 'UK'],
    [false, 'Overseas']
  ]);

  private previousPostcode: string = '';
  private previousHouse: string = '';

  public form = this.fb.group({
    id: [],
    postcode: ['', Validators.required],
    house: ['', Validators.required],
    street: ['', Validators.required],
    line2: [''],
    city: ['', Validators.required],
    country: ['', Validators.required],
    region: [null, Validators.required],
    _isUk: [true]
  });

  public isDetailVisible = false;

  constructor(private fb: UntypedFormBuilder) {
    this.form.valueChanges.subscribe((value) => {
      this.onChangeFn && this.onChangeFn(value);
    });
    this.form.get('_isUk').valueChanges.subscribe((isUK) => {
      if (isUK) {
        this.form.get('country').setValue('UK');
      }

      if (!this.isDetailVisible && !isUK) {
        this.isDetailVisible = true;
      }

      setControlEnabled(this.form.get('region'), isUK);
    });

    this.form.get('postcode').valueChanges.subscribe((postcode) => {
      const currentPostcode = postcode || '';
      if (currentPostcode !== this.previousPostcode && currentPostcode.length > 0) {
        this.previousPostcode = currentPostcode;
        this.addressCleared.emit();
      }
    });

    this.form.get('house').valueChanges.subscribe((house) => {
      const currentHouse = house || '';
      if (currentHouse !== this.previousHouse && currentHouse.length > 0) {
        this.previousHouse = currentHouse;
        this.addressCleared.emit();
      }
    });
  }

  public onAddressSelected(address: Address, postcode: string) {
    this.form.patchValue({
      id: null,
      postcode: postcode,
      house: address.house,
      street: address.street,
      line2: address.line2,
      city: address.city,
      country: address.country,
      region: address.region
    });
    this.isDetailVisible = true;
    this.addressCleared.emit();
  }

  private onChangeFn: (value: Address) => void;

  public writeValue(obj: any): void {
    if (obj) {
      obj = { ...obj };
      obj._isUk = obj.country === 'UK';

      this.form.patchValue(obj, { emitEvent: false });
      this.previousPostcode = obj.postcode || '';
      this.previousHouse = obj.house || '';

      this.isDetailVisible = true;
    }
  }

  public registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  public registerOnTouched(fn: any): void {
    // throw new Error("Method not implemented.");
  }

  public setDisabledState?(isDisabled: boolean): void {
    // throw new Error("Method not implemented.");
  }
}
